/**
 * Pure pre-publish audit runner. Checks are side-effect free and UI agnostic.
 */

import { CHECKS } from './audit-checks.js';
import { generateSlug } from '../export/generators.js';
import {
  assetLivenessCtx,
  assetLivenessLoaded,
  loadAssetLivenessData
} from './asset-liveness-data.js';

export const FULL_AUDIT_CHECK_IDS = Object.freeze([
  ...CHECKS.filter((check) => check.tier === 'full').map((check) => check.id),
  'template-css-owned-assets',
  'asset-reachability'
]);

export function auditPageCoverage(state) {
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  const hydrationFailed = pages
    .filter((page) => page._pendingTemplateRefs?.length)
    .map((page) => page.id);
  return {
    pagesHydrated: pages.length - hydrationFailed.length,
    pagesTotal: pages.length,
    hydrationFailed
  };
}

let livenessAttempted = false;

/**
 * Load the asset-liveness artifact once, if we can.
 *
 * The asset loader is a BROWSER module, and this engine also runs under Node in
 * tests and build scripts. The import is therefore dynamic and the whole thing
 * is swallowed on failure: outside a browser there is no artifact to fetch, and
 * the audit must behave exactly as it did before rather than throw.
 */
async function ensureAssetLiveness() {
  if (livenessAttempted || assetLivenessLoaded()) return;
  livenessAttempted = true;
  try {
    const { loadJSON } = await import('../core/asset-loader.js');
    await loadAssetLivenessData(loadJSON);
  } catch {
    // Expected flow under Node / when the artifact has never been generated.
  }
}

function defaultCtx() {
  return {
    parseHTML: (html) => {
      if (typeof DOMParser === 'undefined') {
        throw new Error('DOMParser is not available');
      }
      return new DOMParser().parseFromString(html, 'text/html');
    },
    generateSlug,
    now: () => Date.now(),
    // Asset liveness reaches checks HERE and only here. runAudit has five
    // callers; threading this through each would mean a missed one silently
    // stops escalating dead hotlinks, a defect with no symptom.
    ...assetLivenessCtx()
  };
}

export function runAudit(state, opts = {}) {
  const tier = opts.tier || 'cheap';
  const ctx = opts.ctx ? { ...defaultCtx(), ...opts.ctx } : defaultCtx();
  const t0 = ctx.now();
  const findings = [];
  const byCheck = {};

  for (const check of CHECKS) {
    if (tier === 'cheap' && check.tier !== 'cheap') continue;
    const out = safeRun(check, state, ctx);
    if (out.length) {
      findings.push(...out);
      byCheck[check.id] = out;
    }
  }

  return finishReport({
    findings,
    byCheck,
    tier,
    ctx,
    t0,
    scope: opts.scope || null,
    state,
    revision: opts.revision
  });
}

function finishReport({ findings, byCheck, tier, ctx, t0, scope = null, state, revision = null }) {
  const countsBySeverity = { blocking: 0, warning: 0, cleanup: 0 };
  for (const finding of findings) {
    countsBySeverity[finding.severity] = (countsBySeverity[finding.severity] || 0) + 1;
  }

  return {
    findings,
    countsBySeverity,
    byCheck,
    tier,
    runDurationMs: ctx.now() - t0,
    timestamp: ctx.now(),
    scope,
    projectId: state?.projectId || null,
    revision,
    tiers: tier === 'cheap' ? ['cheap'] : ['cheap', 'full'],
    coverage: auditPageCoverage(state),
    pending:
      tier === 'cheap'
        ? [...FULL_AUDIT_CHECK_IDS]
        : ['template-css-owned-assets', 'asset-reachability'],
    checkedAt: ctx.now(),
    checkedAtByTier: tier === 'cheap' ? { cheap: ctx.now() } : { cheap: ctx.now(), full: ctx.now() }
  };
}

/**
 * Full audit with a main-thread yield between checks. Generation/abort guards
 * are evaluated on every boundary, so a superseded idle run can never publish
 * stale findings.
 */
export async function runAuditCooperatively(state, opts = {}) {
  const tier = opts.tier || 'cheap';
  // The one async seam both the audit tab and the export snapshot pass through,
  // so this is where the liveness artifact gets loaded. Idempotent, cached, and
  // absent-safe: a missing artifact leaves the check at its previous severity
  // rather than failing the audit.
  await ensureAssetLiveness();
  const ctx = opts.ctx ? { ...defaultCtx(), ...opts.ctx } : defaultCtx();
  const t0 = ctx.now();
  const findings = [];
  const byCheck = {};
  const shouldContinue = opts.shouldContinue || (() => true);
  const yieldToMain = opts.yieldFn || (() => new Promise((resolve) => setTimeout(resolve, 0)));

  for (const check of CHECKS) {
    if (tier === 'cheap' && check.tier !== 'cheap') continue;
    if (opts.signal?.aborted) throw new DOMException('Audit cancelled', 'AbortError');
    if (!shouldContinue()) return null;
    const out = safeRun(check, state, ctx);
    if (out.length) {
      findings.push(...out);
      byCheck[check.id] = out;
    }
    await yieldToMain();
  }
  if (opts.signal?.aborted) throw new DOMException('Audit cancelled', 'AbortError');
  if (!shouldContinue()) return null;
  return finishReport({
    findings,
    byCheck,
    tier,
    ctx,
    t0,
    scope: opts.scope || null,
    state,
    revision: opts.revision
  });
}

export function appendAuditFindings(report, additions) {
  const extra = Array.isArray(additions) ? additions.filter(Boolean) : [];
  if (!extra.length) return report;
  const findings = [...(report?.findings || [])];
  const seen = new Set(findings.map((finding) => finding.id));
  for (const finding of extra) {
    if (!seen.has(finding.id)) findings.push(finding);
  }
  const countsBySeverity = { blocking: 0, warning: 0, cleanup: 0 };
  for (const finding of findings) {
    countsBySeverity[finding.severity] = (countsBySeverity[finding.severity] || 0) + 1;
  }
  const byCheck = {};
  for (const finding of findings) (byCheck[finding.checkId] ||= []).push(finding);
  return { ...report, findings, countsBySeverity, byCheck };
}

function safeRun(check, state, ctx) {
  try {
    return check.run(state, ctx) || [];
  } catch (err) {
    return [
      {
        id: `audit-engine:${check.id}:crashed`,
        checkId: check.id,
        severity: 'warning',
        title: `Audit check "${check.label || check.id}" failed to run`,
        detail: String(err?.message || err),
        location: null,
        fix: null
      }
    ];
  }
}
